import { layerSimulationExpiryDtes } from "@/lib/strategy/simulation-scope";
import type { StrategyLayer } from "@/types/strategy";

export const MAX_SIMULATED_STRATEGIES_PER_EXPIRY = 6;

function capacityOverflowLayers(
  layers: readonly StrategyLayer[],
  fallbackDte: number,
  holidays: Set<string>,
  includeDisabled: boolean,
): StrategyLayer[] {
  const counts = new Map<number, number>();
  const overflow: StrategyLayer[] = [];

  for (const layer of layers) {
    if (!includeDisabled && !layer.enabled) continue;

    let exceedsLimit = false;
    for (const expiryDte of layerSimulationExpiryDtes(
      layer,
      fallbackDte,
      holidays,
    )) {
      const nextCount = (counts.get(expiryDte) ?? 0) + 1;
      counts.set(expiryDte, nextCount);
      exceedsLimit ||= nextCount > MAX_SIMULATED_STRATEGIES_PER_EXPIRY;
    }

    if (exceedsLimit) overflow.push(layer);
  }

  return overflow;
}

export function simulationCapacityOverflowLayers(
  layers: readonly StrategyLayer[],
  fallbackDte: number,
  holidays: Set<string>,
): StrategyLayer[] {
  return capacityOverflowLayers(layers, fallbackDte, holidays, false);
}

export function strategySlotCapacityOverflowLayers(
  layers: readonly StrategyLayer[],
  fallbackDte: number,
  holidays: Set<string>,
): StrategyLayer[] {
  return capacityOverflowLayers(layers, fallbackDte, holidays, true);
}

export function exceedsSimulationCapacity(
  layers: readonly StrategyLayer[],
  fallbackDte: number,
  holidays: Set<string>,
): boolean {
  return (
    simulationCapacityOverflowLayers(layers, fallbackDte, holidays).length > 0
  );
}

export function exceedsStrategySlotCapacity(
  layers: readonly StrategyLayer[],
  fallbackDte: number,
  holidays: Set<string>,
): boolean {
  return (
    strategySlotCapacityOverflowLayers(layers, fallbackDte, holidays).length > 0
  );
}

export function increasesStrategySlotOverflow(
  previousLayers: readonly StrategyLayer[],
  nextLayers: readonly StrategyLayer[],
  fallbackDte: number,
  holidays: Set<string>,
): boolean {
  const countsFor = (layers: readonly StrategyLayer[]) => {
    const counts = new Map<number, number>();
    for (const layer of layers) {
      for (const expiryDte of layerSimulationExpiryDtes(
        layer,
        fallbackDte,
        holidays,
      )) {
        counts.set(expiryDte, (counts.get(expiryDte) ?? 0) + 1);
      }
    }
    return counts;
  };

  const previousCounts = countsFor(previousLayers);
  const nextCounts = countsFor(nextLayers);
  return [...nextCounts].some(
    ([expiryDte, count]) =>
      count > MAX_SIMULATED_STRATEGIES_PER_EXPIRY &&
      count > (previousCounts.get(expiryDte) ?? 0),
  );
}
